"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.redisCache = void 0;
const ioredis_1 = require("ioredis");
const zlib_1 = require("zlib");
const util_1 = require("util");
const logger_1 = require("./logger");
var CircuitBreakerStatus;
(function (CircuitBreakerStatus) {
    CircuitBreakerStatus["OPEN"] = "OPEN";
    CircuitBreakerStatus["CLOSED"] = "CLOSED";
})(CircuitBreakerStatus || (CircuitBreakerStatus = {}));
function redisCache(clientConfig) {
    const zipFn = (0, util_1.promisify)(zlib_1.gzip);
    const unzipFn = (0, util_1.promisify)(zlib_1.gunzip);
    const redisClient = new ioredis_1.Redis.Cluster([
        {
            host: clientConfig.redisConfigurationEndpoint,
            port: clientConfig.redisPort,
        },
    ], {
        scaleReads: 'all',
        redisOptions: {
            commandTimeout: 1000,
            connectTimeout: 1500,
        },
        /**
         * When trying to connect to the cluster if the connection fails, the client will retry to connect to the cluster
         * After 3 retries, with connectionTimeout interval + data returned on this method, the client will be disconnected and set status 'end'
         */
        clusterRetryStrategy: (times, error) => {
            const maxRetries = 3;
            if (times > maxRetries) {
                logger_1.logger.warn({ times, error }, 'Max retries reached');
                return null;
            }
            logger_1.logger.debug({ times, error }, 'Retrying Redis connection');
            return Math.min(100 + times * 2, 2000);
        },
    });
    let circuitBreakerStatus = CircuitBreakerStatus.CLOSED;
    redisClient.on('error', (err) => {
        circuitBreakerStatus = CircuitBreakerStatus.OPEN;
        logger_1.logger.error({ err }, 'Redis connection error');
    });
    async function zip(value) {
        try {
            return await zipFn(value);
        }
        catch (err) {
            logger_1.logger.warn({ err, value }, 'Failed to zip value');
            return value;
        }
    }
    async function unzip(value, key) {
        try {
            return await unzipFn(value);
        }
        catch (err) {
            logger_1.logger.warn({ err, value, key }, 'Failed to unzip value');
            return value;
        }
    }
    function withPrefix(key) {
        return `${clientConfig.envName}:${key}`;
    }
    async function retryConnection() {
        if (redisClient.status !== 'end')
            return;
        try {
            await redisClient.connect();
        }
        catch (err) {
            logger_1.logger.error({ err }, 'Failed to connect to Redis');
        }
    }
    function circuitBreaker(fn, returnStub) {
        return async (...args) => {
            await retryConnection();
            if (redisClient.status === 'ready')
                circuitBreakerStatus = CircuitBreakerStatus.CLOSED;
            if (circuitBreakerStatus === CircuitBreakerStatus.OPEN) {
                logger_1.logger.warn(`Circuit breaker: Redis client is not ready, current status: ${redisClient.status}`);
                return returnStub;
            }
            try {
                return await fn(...Array.from(args));
            }
            catch (err) {
                if (err.failedOnCreation)
                    throw err;
                logger_1.logger.warn({ err }, 'Circuit breaker: Redis client error');
                return returnStub;
            }
        };
    }
    async function get(key) {
        const value = await redisClient.getBuffer(withPrefix(key));
        if (!value)
            return undefined;
        const buff = await unzip(value, key);
        return JSON.parse(buff.toString());
    }
    async function del(key) {
        const value = await redisClient.del(withPrefix(key));
        return value;
    }
    async function getOrCreate(key, creator, options = {}) {
        const cachedValue = await get(key);
        if (cachedValue)
            return cachedValue;
        let value;
        try {
            value = await creator();
        }
        catch (err) {
            err.failedOnCreation = true;
            throw err;
        }
        try {
            await set(key, value, options);
        }
        catch (err) {
            logger_1.logger.warn({ err }, 'Failed to create cache');
        }
        return value;
    }
    async function set(key, value, { ttl } = {}) {
        const buffer = await zip(JSON.stringify(value));
        if (ttl) {
            const ttlValue = typeof ttl === 'string' ? parseInt(ttl, 10) : ttl;
            await redisClient.set(withPrefix(key), buffer, 'EX', ttlValue);
        }
        else {
            await redisClient.set(withPrefix(key), buffer);
        }
        return true;
    }
    async function miss(key, value, options = {}) {
        if (await get(key))
            return false;
        return set(key, value, options);
    }
    async function flushAll() {
        await redisClient.flushall();
    }
    return {
        get: circuitBreaker(get, undefined),
        set: circuitBreaker(set, false),
        del: circuitBreaker(del, undefined),
        getOrCreate: circuitBreaker(getOrCreate),
        miss: circuitBreaker(miss, false),
        flushAll: circuitBreaker(flushAll, undefined),
    };
}
exports.redisCache = redisCache;
//# sourceMappingURL=redis-cache.js.map